import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';

interface BundleResponse {
  url: string;
  session_id: string;
  bundle: {
    id: string;
    name: string;
    credits: number;
    price_pence: number;
    charge_pence?: number;
    discount_saving_pence?: number;
    discount_code?: string | null;
  };
}

/**
 * Bundle payment screen. Creates a Stripe Checkout Session on the server
 * and redirects the customer straight to Stripe's hosted page. This
 * works in every webview (Instagram, Facebook, TikTok) — no iframe.
 */
export function CreditsPayment() {
  const location = useLocation();
  const navigate = useNavigate();
  const bundleId = useMemo(
    () => new URLSearchParams(location.search).get('bundleId'),
    [location.search]
  );
  const [error, setError] = useState<string | null>(null);
  const [discountInput, setDiscountInput] = useState('');
  const [discountErr, setDiscountErr] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!bundleId) navigate('/credits', { replace: true });
  }, [bundleId, navigate]);

  async function go(discountCode?: string) {
    if (!bundleId) return;
    setWorking(true);
    setError(null);
    setDiscountErr(null);
    try {
      const res = await apiFetch<BundleResponse>('/api/checkout/credit-purchase', {
        method: 'POST',
        body: JSON.stringify({
          bundle_id: bundleId,
          discount_code: discountCode || undefined
        })
      });
      window.location.href = res.url;
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message === 'stripe_disabled'
            ? "Card payments aren't switched on yet. Get in touch and we'll add credits manually."
            : e.message
          : 'Could not start payment';
      if (discountCode) {
        setDiscountErr(msg);
      } else {
        setError(msg);
      }
      setWorking(false);
    }
  }

  return (
    <section className="screen">
      <div className="scroll narrow-scroll">
        <div className="ck-head">
          <h1>PAYMENT</h1>
          <div className="p">You'll be redirected to Stripe to complete payment securely.</div>
        </div>

        {error && (
          <div className="empty-grid">
            <div className="h">Couldn't start checkout</div>
            <div className="p">{error}</div>
            <Link to="/credits" className="chip" style={{ background: 'var(--ink)', color: '#fff' }}>
              ← Back to bundles
            </Link>
          </div>
        )}

        {!error && (
          <>
            <div style={{ padding: '14px 22px 0' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 11,
                  letterSpacing: '0.12em',
                  color: 'var(--ink-soft)',
                  marginBottom: 6,
                  fontWeight: 700
                }}
              >
                DISCOUNT CODE (OPTIONAL)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={discountInput}
                  onChange={(e) => {
                    setDiscountInput(e.target.value.toUpperCase());
                    if (discountErr) setDiscountErr(null);
                  }}
                  placeholder="Enter code"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  disabled={working}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    fontSize: 14,
                    letterSpacing: '0.05em'
                  }}
                />
              </div>
              {discountErr && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: 'var(--danger, #b3261e)'
                  }}
                >
                  {discountErr}
                </div>
              )}
            </div>

            <div className="footer-cta pad-bottom" style={{ paddingTop: 18 }}>
              <button
                type="button"
                className="cta-full"
                disabled={working}
                onClick={() => void go(discountInput.trim() || undefined)}
              >
                <span>{working ? 'OPENING STRIPE…' : 'CONTINUE TO PAYMENT'}</span>
              </button>
              <div
                className="micro"
                style={{
                  fontSize: 11,
                  color: 'var(--ink-soft)',
                  textAlign: 'center',
                  marginTop: 12
                }}
              >
                Secured by Stripe. Apple Pay & Google Pay available.
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
